import { makePersisted } from "@solid-primitives/storage";
import { createMemo, createSignal } from "solid-js";
import { useUserMode } from "~/hooks/useUserMode";

/** How keyboard shortcuts behave: visible hints, hidden hints but active, or fully off. */
type KeyboardShortcutPreference = "show" | "hide" | "disable";

const STORAGE_KEY = "keyboard-shortcut-preference";
const LEGACY_BOOL_KEY = "keyboard-shortcut-suggestions-enabled";

function parseStoredPreference(raw: string | null): KeyboardShortcutPreference | undefined {
  if (raw == null) return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    const v: unknown = Array.isArray(parsed) ? parsed[0] : parsed;
    if (v === "show" || v === "hide" || v === "disable") return v;
  } catch {
    /* ignore */
  }
  return undefined;
}

function readInitialStoredKeyboard(): KeyboardShortcutPreference | null {
  if (typeof window === "undefined") return null;
  try {
    const fromNew = parseStoredPreference(localStorage.getItem(STORAGE_KEY));
    if (fromNew !== undefined) return fromNew;
    const legacyRaw = localStorage.getItem(LEGACY_BOOL_KEY);
    if (legacyRaw != null) {
      const parsed = JSON.parse(legacyRaw) as unknown;
      const boolVal: unknown = Array.isArray(parsed) ? parsed[0] : parsed;
      try {
        localStorage.removeItem(LEGACY_BOOL_KEY);
      } catch {
        /* ignore */
      }
      return boolVal === true ? "show" : "hide";
    }
  } catch {
    /* fall through */
  }
  return null;
}

const [storedKeyboard, setStoredKeyboard] = makePersisted(
  createSignal<KeyboardShortcutPreference | null>(readInitialStoredKeyboard()),
  {
    storage: typeof window !== "undefined" ? localStorage : undefined,
    name: STORAGE_KEY,
  }
);

/**
 * Keyboard shortcut preference: Show, Hide, or Disable.
 * When storage is unset (null), defaults: Regular mode → Hide, Advanced mode → Show.
 * Migrates legacy boolean `keyboard-shortcut-suggestions-enabled` to an explicit value.
 */
export const useKeyboardShortcutSuggestions = () => {
  const { userMode } = useUserMode();

  const preference = createMemo((): KeyboardShortcutPreference => {
    const s = storedKeyboard();
    if (s !== null) return s;
    return userMode() === "advanced" ? "show" : "hide";
  });

  const setPreference = (v: KeyboardShortcutPreference) => {
    setStoredKeyboard(v);
  };

  const showShortcutHints = () => preference() === "show";
  const shortcutsActive = () => preference() !== "disable";

  return {
    preference,
    setPreference,
    showShortcutHints,
    shortcutsActive,
  };
};
